import type { ReactNode } from 'react'
import type { SkillDefinition } from '@shared/types'
import { TileFrame } from './TileFrame'
import { SkillGlyph } from '@shared/components/SkillGlyph'

interface SkillTileProps {
  skill: SkillDefinition
  /** The tile's colour, as an `r, g, b` triple from the mark's own spectrum. */
  glow: string
  /** A count in the corner: what waits there (the Review Queue, 2026-10-15). */
  badge?: string
  /** A colour the person chose for this card (2026-10-21): its edge shows it all the time. */
  coloured?: boolean
  /** The card's own buttons, drawn inside it so they move with it. */
  children?: ReactNode
}

/**
 * One tile in the overview's skill grid.
 *
 * A skill without a route is not built yet. The tile says so with its badge
 * and does not open — and that is the whole of the difference. It is drawn at
 * the same brightness as the rest, in the same colours, and lights up the same
 * way, because the grid is one surface and a row of dimmed tiles reads as a
 * page that failed to load rather than as work still to come.
 *
 * Not pinned any more: the overview's cards are dragged into place
 * (Huanrui, 2026-10-02), and a card waiting to be built can be moved like
 * any other.
 */
export function SkillTile({ skill, glow, badge, coloured = false, children }: SkillTileProps) {
  const open = Boolean(skill.route)

  return (
    <TileFrame
      glow={glow}
      to={open ? (skill.route as string) : undefined}
      label={skill.name}
      className={`${open ? '' : 'ww-tile--soon'}${coloured ? ' ww-tile--coloured' : ''}`.trim()}
    >
      <span className="ww-tile__glyph" aria-hidden="true">
        <SkillGlyph glyph={skill.glyph} />
      </span>
      <span className="ww-tile__name">{skill.name}</span>
      <span className="ww-tile__summary">{skill.summary}</span>
      {!open ? <span className="ww-tile__soon">Soon</span> : badge ? <span className="ww-tile__soon ww-tile__count">{badge}</span> : null}
      {children}
    </TileFrame>
  )
}
